import { Middleware, AnyAction } from '@reduxjs/toolkit';

/**
 * Cross-tab sync storage key
 */
const CROSS_TAB_SYNC_KEY = 'absolvire-cross-tab-sync';

/**
 * Sync data structure stored in localStorage
 */
export interface CrossTabSyncData {
  timestamp: number;
  action: {
    type: string;
    payload?: any;
  };
  tabId: string;
}

/**
 * Generate a unique tab ID for this browser tab
 */
const getTabId = (): string => {
  let tabId = sessionStorage.getItem('absolvire-tab-id');
  if (!tabId) {
    tabId = `tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('absolvire-tab-id', tabId);
  }
  return tabId;
};

/**
 * Actions to sync across tabs
 * Only sync specific actions that modify shared state
 *
 * NOTE: WebSocket-triggered actions are NOT included here because
 * WebSocket already broadcasts to all connected tabs, so syncing them
 * would cause duplicate updates and bouncing.
 */
const SYNCABLE_ACTIONS = new Set([
  // Folders slice
  'folders/updateFolderFile/fulfilled',
  'folders/deleteFolderFile/fulfilled',
  'folders/duplicateFolderFile/fulfilled',
  'folders/addFileToFolder/fulfilled',
  'folders/deleteFolder/fulfilled',
  'folders/sendFileEmail/fulfilled',
  'folders/fetchAndAddFolder/fulfilled',
  'folders/updateClientFile/fulfilled',
  // 'folders/updateFileFromWebSocket' - EXCLUDED: WebSocket already broadcasts
  'folders/setFolders',

  // Files slice
  'files/updateFile/fulfilled',
  'files/deleteFile/fulfilled',

  // Models slice
  'models/createModel/fulfilled',
  'models/updateModel/fulfilled',
  'models/deleteModel/fulfilled',

  // Filters slice
  'filters/createFilter/fulfilled',
  'filters/updateFilter/fulfilled',
  'filters/deleteFilter/fulfilled',

  // Settings slice
  'settings/updateSettings/fulfilled',

  // Dashboard slice
  'dashboard/updateDashboard/fulfilled',

  // Uploads slice
  'uploads/uploadFile/fulfilled',
  'uploads/deleteUploadedFile/fulfilled',
]);

/**
 * Meta flag to mark actions that came from cross-tab sync
 * This prevents re-syncing actions that were already synced
 */
export const CROSS_TAB_SYNC_META = '__crossTabSync';

/**
 * Redux middleware to sync actions across tabs
 */
export const crossTabSyncMiddleware: Middleware =
  () => (next) => (action: unknown) => {
    const result = next(action);
    const actionObj = action as AnyAction;
    const actionType = actionObj.type;

    // Don't sync if this action came from cross-tab sync (prevent bouncing)
    if (actionObj.meta?.[CROSS_TAB_SYNC_META]) {
      return result;
    }

    // Only sync specific actions
    if (actionType && SYNCABLE_ACTIONS.has(actionType)) {
      try {
        const syncData: CrossTabSyncData = {
          timestamp: Date.now(),
          action: {
            type: actionType,
            payload: actionObj.payload,
          },
          tabId: getTabId(),
        };

        // Store in localStorage (triggers storage event in other tabs)
        localStorage.setItem(CROSS_TAB_SYNC_KEY, JSON.stringify(syncData));
      } catch (error) {
        // Silently fail if localStorage is not available (e.g., private browsing)
        console.warn('Failed to sync action across tabs:', error);
      }
    }

    return result;
  };

/**
 * Get the current sync data from localStorage
 */
export const getCrossTabSyncData = (): CrossTabSyncData | null => {
  try {
    const data = localStorage.getItem(CROSS_TAB_SYNC_KEY);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
};

/**
 * Get the current tab ID
 */
export const getCurrentTabId = (): string => {
  return getTabId();
};

export { CROSS_TAB_SYNC_KEY };
